import React, { useRef, useState, useCallback } from "react";
import Popup from "../../common/Popup/Popup";
import SignatureField from "../../common/FormFields/SignatureField";
import PrimaryButton from "../../common/Buttons/PrimaryButton";
import { ArrowLeft } from "lucide-react";

/**
 * Sign trainer or trainee signature — matches Training Log design (pad, password, outside-system flag).
 */
const TrainingLogSignatureModal = ({
  closeModal,
  role = "trainee",
  partNumber = "",
  revision = "",
  trainerName = "",
  traineeName = "",
  onConfirm,
}) => {
  const [password, setPassword] = useState("");
  const [signedOutsideSystem, setSignedOutsideSystem] = useState(false);
  const sigRef = useRef(null);

  const roleLabel = role === "trainer" ? "Trainer" : "Trainee";
  const titleText = `Sign As ${roleLabel} for ${partNumber || "—"} Rev ${revision || "—"}`;

  const handleConfirm = useCallback(async () => {
    const pw = password.trim();
    if (!pw) {
      await Promise.resolve(onConfirm?.({ error: "Password is required." }));
      return;
    }
    if (!signedOutsideSystem) {
      if (sigRef.current?.isEmpty?.()) {
        await Promise.resolve(onConfirm?.({ error: "Signature is required." }));
        return;
      }
    }
    const canvas = sigRef.current?.getCanvas?.();
    const signatureDataUrl = signedOutsideSystem
      ? ""
      : canvas?.toDataURL?.("image/png") ?? "";
    try {
      const result = await Promise.resolve(
        onConfirm?.({
          password: pw,
          signatureDataUrl,
          signedOutsideSystem,
          role,
        }),
      );
      if (result?.error) return;
      closeModal?.(false);
    } catch {
      /* parent handles */
    }
  }, [password, signedOutsideSystem, onConfirm, closeModal, role]);

  const fieldColumnStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  };

  const signatureFieldStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    marginLeft: "-1px",
  };

  const signaturePadCanvasStyle = {
    maxWidth: "100%",
    boxSizing: "border-box",
  };

  const footerBtnStyle = {
    padding: "8px 20px",
    fontSize: "13px",
    minHeight: "36px",
    borderRadius: "4px",
  };

  const customHeading = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        width: "100%",
        minHeight: 40,
        paddingLeft: "2.75rem",
        paddingRight: "2.75rem",
        boxSizing: "border-box",
      }}
    >
      <button
        type="button"
        aria-label="Back"
        onClick={() => closeModal?.(false)}
        style={{
          position: "absolute",
          left: 0,
          top: "50%",
          transform: "translateY(-50%)",
          width: 36,
          height: 36,
          borderRadius: "50%",
          border: "none",
          background: "#e7f1ff",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ArrowLeft size={20} color="#0D6EFD" strokeWidth={2.25} />
      </button>
      <h2
        style={{
          margin: 0,
          fontSize: "1.05rem",
          fontWeight: 600,
          textAlign: "center",
          lineHeight: 1.3,
        }}
      >
        {titleText}
      </h2>
    </div>
  );

  return (
    <Popup
      closeModal={closeModal}
      title={null}
      titleStyle={{ display: "none" }}
      customHeading={customHeading}
      headerStyle={{
        textAlign: "center",
        borderBottom: "1px solid #dee2e6",
        paddingBottom: "12px",
        paddingTop: "4px",
      }}
      width={{
        width: "900px",
        maxWidth: "min(900px, calc(100vw - 32px))",
        boxSizing: "border-box",
      }}
      shouldScroll
    >
      <div
        style={{
          padding: "12px 20px 24px",
          maxHeight: "min(82vh, 900px)",
          overflowY: "auto",
          ...fieldColumnStyle,
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px 16px",
            marginBottom: "16px",
            fontSize: "12px",
            color: "#212529",
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 20px" }}>
            <span>
              <strong>Subassembly:</strong>{" "}
              <span style={{ textDecoration: "underline", fontWeight: 600 }}>
                {partNumber || "—"} Rev {revision || "—"}
              </span>
            </span>
            <span>
              <strong>Trainer:</strong>{" "}
              <span style={{ textDecoration: "underline", fontWeight: 600 }}>
                {trainerName || "—"}
              </span>
            </span>
            <span>
              <strong>Trainee:</strong>{" "}
              <span style={{ textDecoration: "underline", fontWeight: 600 }}>
                {traineeName || "—"}
              </span>
            </span>
          </div>
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              userSelect: "none",
              whiteSpace: "nowrap",
            }}
          >
            <input
              type="checkbox"
              checked={signedOutsideSystem}
              onChange={(e) => {
                setSignedOutsideSystem(e.target.checked);
                if (e.target.checked) {
                  sigRef.current?.clear?.();
                }
              }}
            />
            <span>Training was Signed Outside of System</span>
          </label>
        </div>

        <div
          style={{
            marginBottom: "16px",
            opacity: signedOutsideSystem ? 0.45 : 1,
            pointerEvents: signedOutsideSystem ? "none" : "auto",
            ...fieldColumnStyle,
          }}
        >
          <SignatureField
            ref={sigRef}
            label="Sign Here"
            required={!signedOutsideSystem}
            width="100%"
            height={280}
            style={signatureFieldStyle}
            canvasStyle={signaturePadCanvasStyle}
            labelStyle={{ fontSize: "12px", fontWeight: 600 }}
          />
        </div>

        <div style={{ marginBottom: "20px", ...fieldColumnStyle }}>
          <label
            style={{
              display: "block",
              fontSize: "12px",
              marginBottom: "6px",
              color: "#212529",
              fontWeight: 600,
            }}
          >
            Enter Password
          </label>
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            style={{
              width: "40%",
              boxSizing: "border-box",
              padding: "10px 12px",
              border: "1px solid #CED4DA",
              borderRadius: "5px",
              fontSize: "13px",
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "8px",
          }}
        >
          <PrimaryButton
            type="button"
            onClick={handleConfirm}
            style={{ ...footerBtnStyle, minWidth: 100 }}
          >
            Confirm
          </PrimaryButton>
        </div>
      </div>
    </Popup>
  );
};

export default TrainingLogSignatureModal;
